import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@istari-frontend/ui";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";

import { ChatClassifyFields } from "~/ai-chat/ui/chat-classify-fields";
import type {
  InfosecLevelOption,
  SaveDestinationOption,
  SaveResourceCardChoices,
} from "~/ai-chat/ui/chat-save-resource-card";

interface ChatSaveTranscriptDialogProps {
  open: boolean;
  defaultName: string;
  // undefined = the platform infosec setting hasn't resolved yet; saving stays
  // blocked so the level picker is on screen before anything is written.
  infosecEnabled: boolean | undefined;
  infosecLevels: InfosecLevelOption[];
  defaultInfosecLevel?: InfosecLevelOption | null;
  // The system the chat is open on. null = library only; undefined = still resolving,
  // which also blocks saving so the transcript never lands in the library by accident.
  destination: SaveDestinationOption | null | undefined;
  controlTagsField?: ReactNode;
  isSaving: boolean;
  onSave: (name: string, choices: SaveResourceCardChoices) => void;
  onCancel: () => void;
}

export function ChatSaveTranscriptDialog({
  open,
  defaultName,
  infosecEnabled,
  infosecLevels,
  defaultInfosecLevel,
  destination,
  controlTagsField,
  isSaving,
  onSave,
  onCancel,
}: ChatSaveTranscriptDialogProps) {
  const destinationSelectId = useId();
  const [name, setName] = useState(defaultName);
  const [infosecLevelId, setInfosecLevelId] = useState<string | null>(null);
  const [saveToSystem, setSaveToSystem] = useState(true);

  // Reset only on the closed→open edge — not on every `defaultName` change, which
  // is derived from the streaming transcript and would clobber a name the user is
  // editing while the dialog is open.
  const prevOpenRef = useRef(open);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setName(defaultName);
      setInfosecLevelId(null);
      setSaveToSystem(true);
    }
    prevOpenRef.current = open;
  }, [open, defaultName]);

  const trimmedName = name.trim();
  const savesToSystem = destination !== null && saveToSystem;
  const canSave = trimmedName.length > 0 && infosecEnabled !== undefined && destination !== undefined && !isSaving;

  const handleSave = () => {
    if (!canSave) return;
    onSave(trimmedName, { infosecLevelId: infosecLevelId ?? undefined, saveToSystem: savesToSystem });
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !isSaving && onCancel()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Export chat to library</DialogTitle>
          <DialogDescription>
            {savesToSystem && destination
              ? `The transcript is saved as a new resource and added to ${destination.systemName} on ${destination.branchName}.`
              : "The transcript is saved as a new resource in your library."}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <ChatClassifyFields
            name={name}
            onNameChange={setName}
            showInfosecLevel={infosecEnabled === true}
            infosecLevels={infosecLevels}
            defaultInfosecLevel={defaultInfosecLevel}
            infosecLevelId={infosecLevelId}
            onInfosecLevelChange={setInfosecLevelId}
          />
          {controlTagsField}
          {destination ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={destinationSelectId} className="text-xs">
                Save to
              </Label>
              <Select
                value={saveToSystem ? "system" : "library"}
                onValueChange={(value) => setSaveToSystem(value === "system")}
                disabled={isSaving}
              >
                <SelectTrigger id={destinationSelectId}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="system">
                    {destination.systemName} · {destination.branchName}
                  </SelectItem>
                  <SelectItem value="library">Your library</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={!canSave}>
            {isSaving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
